/**
 * Push-notification tap → mobile route.
 *
 * The server puts a WEB link in the APNs payload as `deep_link`
 * (internal/notify/apns.go), e.g.
 *   https://app.usetenon.com/projects/<uuid>/logs/<logId>
 *   https://app.usetenon.com/projects/<uuid>/task-schedule?peek=<taskId>&peek_kind=task
 * The native app only routes /m/* paths — anything else falls through to
 * MobileComingSoon. This translates the web link into the matching mobile
 * screen, and never returns a path that would land on "Coming soon".
 */

const FALLBACK = '/m/today'

function toPathAndQuery(raw: string): { path: string; params: URLSearchParams } | null {
  if (!raw) return null
  try {
    const u = raw.startsWith('/') ? new URL(raw, 'https://app.invalid') : new URL(raw)
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null
    return { path: u.pathname.replace(/\/+$/, '') || '/', params: u.searchParams }
  } catch {
    return null
  }
}

export function mobilePathForPushLink(raw: string | null | undefined): string | null {
  const parsed = toPathAndQuery(raw ?? '')
  if (!parsed) return null
  const { path, params } = parsed
  let m: RegExpMatchArray | null

  if (path.startsWith('/m/')) return path + (params.toString() ? `?${params}` : '')

  // Task peek links: /projects/:pid/<any view>?peek=<taskId>&peek_kind=task
  const peek = params.get('peek')
  if (peek && (params.get('peek_kind') ?? 'task') === 'task') return `/m/tasks/${encodeURIComponent(peek)}`

  if ((m = path.match(/^\/projects\/([^/]+)\/logs\/([^/]+)$/))) {
    return `/m/p/${m[1]}/log?log=${encodeURIComponent(m[2])}`
  }
  if ((m = path.match(/^\/projects\/([^/]+)\/(rfis?)(\/|$)/))) return `/m/p/${m[1]}/rfi`
  if ((m = path.match(/^\/projects\/([^/]+)\/(change-orders|co)(\/|$)/))) return `/m/p/${m[1]}/co`
  if ((m = path.match(/^\/projects\/([^/]+)\/(schedule|task-schedule|tasks)(\/|$)/))) return `/m/p/${m[1]}/tasks`
  if ((m = path.match(/^\/projects\/([^/]+)(\/|$)/))) return `/m/p/${m[1]}`

  // Project-less log link (notifications sent before #4283 / comment + mention
  // events): the Reports screen finds the log's project and opens it.
  if ((m = path.match(/^\/logs\/([^/]+)$/))) return `/m/reports?log=${encodeURIComponent(m[1])}`
  if (/^\/logs(\/|$)/.test(path)) return '/m/reports'
  if ((m = path.match(/^\/tasks\/([^/]+)$/))) return `/m/tasks/${m[1]}`
  if (/^\/tasks(\/|$)/.test(path)) return '/m/tasks'
  if ((m = path.match(/^\/invoices\/([^/]+)/))) return `/m/invoices/${m[1]}`
  if (/^\/invoices(\/|$)/.test(path)) return '/m/invoices'
  if ((m = path.match(/^\/estimates\/([^/]+)$/))) return `/m/estimates/${m[1]}`
  if (/^\/estimates(\/|$)/.test(path)) return '/m/estimates'
  if ((m = path.match(/^\/proposals\/([^/]+)$/))) return `/m/proposals/${m[1]}`
  if (/^\/proposals(\/|$)/.test(path)) return '/m/proposals'
  if (/^\/rfis(\/|$)/.test(path)) return '/m/rfis'
  if (/^\/change-orders(\/|$)/.test(path)) return '/m/change-orders'
  if (/^\/purchase-orders(\/|$)/.test(path)) return '/m/purchase-orders'
  if (/^\/bills(\/|$)/.test(path)) return '/m/bills'
  if (/^\/(messages|team-chat)(\/|$)/.test(path)) return '/m/messages'
  if (/^\/submittals(\/|$)/.test(path)) return '/m/submittals'
  if (/^\/(timesheets|time)(\/|$)/.test(path)) return '/m/timesheets'

  if (/^\/notifications(\/|$)/.test(path)) return '/m/notifications'

  // Anything unrecognised opens Today rather than "Coming soon".
  return FALLBACK
}

/**
 * Where tapping a row in the mobile notifications list goes (#4282).
 * Prefers the row's stored link (same mapping as a push tap, so both land on
 * the same screen); otherwise routes by entity/event type. Returns null when
 * nothing fits — the caller stays on the list rather than guessing.
 */
export function mobileRouteForNotification(n: {
  link_url?: string | null
  entity_type?: string | null
  event_type?: string | null
  entity_id?: string | number | null
}): string | null {
  if (n.link_url) {
    const p = mobilePathForPushLink(n.link_url)
    if (p && p !== FALLBACK) return p
  }
  const entity = (n.entity_type || '').toLowerCase()
  const evt = (n.event_type || '').toLowerCase()
  const id = n.entity_id != null && n.entity_id !== '' ? encodeURIComponent(String(n.entity_id)) : null
  const is = (prefix: string, ...entities: string[]) => evt.startsWith(prefix) || entities.includes(entity)

  if (is('task_', 'task', 'app_task')) return id ? `/m/tasks/${id}` : '/m/tasks'
  if (is('rfi_', 'rfi')) return '/m/rfis'
  if (is('change_order_', 'change_order')) return '/m/change-orders'
  if (is('invoice_', 'invoice')) return id ? `/m/invoices/${id}` : '/m/invoices'
  if (is('estimate_', 'estimate')) return id ? `/m/estimates/${id}` : '/m/estimates'
  if (is('proposal_', 'proposal')) return id ? `/m/proposals/${id}` : '/m/proposals'
  if (is('po_', 'purchase_order')) return '/m/purchase-orders'
  if (is('bill_', 'bill')) return '/m/bills'
  if (is('submittal_', 'submittal')) return '/m/submittals'
  if (is('timesheet_', 'timesheet')) return '/m/timesheets'
  if (is('daily_log', 'log', 'daily_log', 'daily_report') || evt.startsWith('log_') || evt.startsWith('daily_report')) return '/m/reports'
  if (is('new_message', 'message', 'chat_message') || evt === 'user_mentioned') return '/m/messages'
  return null
}
